import React, { useState, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useApiClient } from "../utils/apiClient";
import { USER_COLORS, COLOR_CLASSES } from "../constants/colors";

const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function localDateString(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function getMonthDays(year, month) {
    const date = new Date(year, month, 1);
    const days = [];
    while (date.getMonth() === month) {
        days.push(new Date(date));
        date.setDate(date.getDate() + 1);
    }
    return days;
}

function getSunday(d) {
    d = new Date(d);
    var day = d.getDay(),
        diff = d.getDate() - day;
    return new Date(d.setDate(diff));
}

function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

function formatDate(date) {
    return `${date.getDate()} ${date.toLocaleString("default", { month: "short" })}`;
}

function formatDateFull(date) {
    const month = date.toLocaleString("default", { month: "long" });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
}

function toISODate(date) {
    return date.getFullYear() + '-' +
        String(date.getMonth() + 1).padStart(2, '0') + '-' +
        String(date.getDate()).padStart(2, '0');
}

function getWeekEndDate(weekStartDate) {
    const startDate = new Date(weekStartDate);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    return toISODate(endDate);
}

function format12h(hour, min) {
    const h = hour % 12 === 0 ? 12 : hour % 12;
    const ampm = hour < 12 ? "AM" : "PM";
    return `${h}:${min} ${ampm}`;
}
const times = Array.from({ length: 24 }, (_, i) => {
    return format12h(i, "00");
});

function getWeeksOfMonth(year, month) {
    const days = getMonthDays(year, month);
    const firstDay = new Date(year, month, 1).getDay();
    let week = Array(firstDay).fill(null);
    const weeks = [];
    days.forEach((date) => {
        if (week.length === 7) {
            weeks.push(week);
            week = [];
        }
        week.push(date);
    });
    while (week.length < 7) week.push(null);
    weeks.push(week);
    return weeks;
}

function timeToDecimal(timeStr) {
    const [h, m] = timeStr.split(":").map(Number);
    return h + m / 60;
}

function formatTimeStr(timeStr) {
    if (!timeStr) return '';
    if (typeof timeStr === 'number') {
        const h = Math.floor(timeStr);
        const m = Math.round((timeStr - h) * 60);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
    }
    if (typeof timeStr === 'string' && timeStr.includes(':')) {
        const [hStr, mStr] = timeStr.split(":");
        const h = parseInt(hStr, 10);
        const m = parseInt(mStr, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
    }
    return '';
}

function normalizeTime(t) {
    if (typeof t === 'number') {
        const h = Math.floor(t);
        const m = Math.round((t - h) * 60);
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
}

function getShiftType(startTime, endTime) {
    if (!startTime || !endTime) return 'SHIFT';

    try {
        let startHour, startMinute, endHour, endMinute;

        if (typeof startTime === 'string' && startTime.includes(':')) {
            [startHour, startMinute] = startTime.split(':').map(Number);
        } else if (typeof startTime === 'number') {
            startHour = Math.floor(startTime);
            startMinute = (startTime % 1) * 60;
        } else {
            return 'SHIFT';
        }

        if (typeof endTime === 'string' && endTime.includes(':')) {
            [endHour, endMinute] = endTime.split(':').map(Number);
        } else if (typeof endTime === 'number') {
            endHour = Math.floor(endTime);
            endMinute = (endTime % 1) * 60;
        } else {
            return 'SHIFT';
        }

        const startMinutes = startHour * 60 + startMinute;
        const endMinutes = endHour * 60 + endMinute;

        if (endMinutes < startMinutes) {
            endMinutes += 24 * 60;
        }

        if (startMinutes >= 420 && endMinutes <= 900) {
            return 'DAY SHIFT';
        }

        if (startMinutes >= 900 && endMinutes <= 1380) {
            return 'EVENING SHIFT';
        }

        if ((startMinutes >= 1380 && endMinutes <= 1380 + 24 * 60) ||
            (startMinutes >= 0 && endMinutes <= 420) ||
            (startMinutes >= 1380 && endMinutes <= 420 + 24 * 60)) {
            return 'NIGHT SHIFT';
        }

        return 'SHIFT';
    } catch (e) {
        return 'SHIFT';
    }
}

function normalizeRepeat(r) {
    if (!r || r === 'none') return '';
    return String(r);
}

function calculateEventPosition(event, eventIndex, dayEvents) {
    const conflictingEvents = dayEvents.filter((otherEvent, otherIndex) => {
        if (otherIndex === eventIndex) return false;
        return event.start < otherEvent.end && event.end > otherEvent.start;
    });

    if (conflictingEvents.length === 0) {
        return {
            left: 0,
            width: '100%',
            zIndex: 10,
            textAlign: 'top',
            isConflicting: false
        };
    }

    const allConflicting = [event, ...conflictingEvents];

    allConflicting.sort((a, b) => {
        if (a.start !== b.start) return a.start - b.start;
        return (a.user_id || '').localeCompare(b.user_id || '');
    });

    const totalConflicting = allConflicting.length;
    const eventWidth = 100 / totalConflicting;

    const positionInConflict = allConflicting.findIndex(e =>
        e.start === event.start &&
        e.end === event.end &&
        e.centerName === event.centerName &&
        e.user_id === event.user_id &&
        e.room_name === event.room_name &&
        e.details === event.details
    );

    const finalPosition = positionInConflict >= 0 ? positionInConflict : 0;
    const left = finalPosition * eventWidth;

    return {
        left: `${left}%`,
        width: `${eventWidth}%`,
        zIndex: 10,
        textAlign: 'top',
        isConflicting: true,
        totalConflicting: totalConflicting,
        positionInCollision: finalPosition
    };
}

function getEventColor(event) {
    const colors = USER_COLORS;
    
    if (event.isAvailable) return 'green';
    if (event.isUnavailable) return 'red';
    
    if (event.centerName) {
        const hash = event.centerName.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
        return colors[Math.abs(hash) % colors.length];
    }
    
    return 'gray';
}

const Schedule = () => {
    const today = new Date();
    const apiClient = useApiClient();
    const [selectedDate, setSelectedDate] = useState(getSunday(today));
    const [hoveredWeekIdx, setHoveredWeekIdx] = useState(null);
    const [hoveredEvent, setHoveredEvent] = useState(null);
    const [eventDetailsModal, setEventDetailsModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [unavailability, setUnavailability] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [availability, setAvailability] = useState([]);
    const [sidebarMonth, setSidebarMonth] = useState(today.getMonth());
    const [sidebarYear, setSidebarYear] = useState(today.getFullYear());
    
    const [hoveredAvailUnavail, setHoveredAvailUnavail] = useState(null);
    
    const [assignments, setAssignments] = useState([]);
    const [centers, setCenters] = useState([]);
    const [centerMap, setCenterMap] = useState({});
    
    const [isMonthlyView, setIsMonthlyView] = useState(false);
    const [monthlyViewDate, setMonthlyViewDate] = useState(today);
    const [monthlyAssignments, setMonthlyAssignments] = useState([]);

    const fetchWeeklyData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiClient.get("/user_profile");
            const userId = res.user_id || (res.profile && res.profile.user_id);
            
            if (res && res.availability) {
                setAvailability(res.availability);
            } else if (res && res.profile && res.profile.availability) {
                setAvailability(res.profile.availability);
            } else {
                setAvailability([]);
            }
            if (res && res.unavailability) {
                setUnavailability(res.unavailability);
            } else if (res && res.profile && res.profile.unavailability) {
                setUnavailability(res.profile.unavailability);
            } else {
                setUnavailability([]);
            }

            const sunday = getSunday(selectedDate);
            const weekStartDate = toISODate(sunday);
            
            const apptRes = await apiClient.get("/assignments", { 
                params: { 
                    user_id: userId,
                    start_date: weekStartDate,
                    end_date: getWeekEndDate(weekStartDate)
                } 
            });
            setAssignments(apptRes.assignments || []);
            
            const centersRes = await apiClient.get("/centers");
            setCenters(centersRes.centers || []);
            const map = {};
            (centersRes.centers || []).forEach(c => { map[c.center_id] = c.name; });
            setCenterMap(map);
        } catch (e) {
            setError("Failed to load schedule");
            setUnavailability([]);
            setAvailability([]);
            setAssignments([]);
            setCenters([]);
            setCenterMap({});
        } finally {
            setLoading(false);
        }
    };

    const fetchMonthlyData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiClient.get("/user_profile");
            const userId = res.user_id || (res.profile && res.profile.user_id);
            
            const year = monthlyViewDate.getFullYear();
            const month = monthlyViewDate.getMonth() + 1;
            const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
            const endDate = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
            
            const apptRes = await apiClient.get("/assignments", { 
                params: { 
                    user_id: userId,
                    start_date: startDate,
                    end_date: endDate
                } 
            });
            setMonthlyAssignments(apptRes.assignments || []);
            
            const centersRes = await apiClient.get("/centers");
            setCenters(centersRes.centers || []);
            const map = {};
            (centersRes.centers || []).forEach(c => { map[c.center_id] = c.name; });
            setCenterMap(map);
        } catch (e) {
            setError("Failed to load monthly schedule");
            setMonthlyAssignments([]);
            setCenters([]);
            setCenterMap({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isMonthlyView) {
            fetchMonthlyData();
        } else {
            fetchWeeklyData();
        }
    }, [selectedDate, monthlyViewDate, isMonthlyView]);





    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const weeks = getWeeksOfMonth(year, month);
    const selectedWeekIdx = weeks.findIndex((week) =>
        week.some((d) => d && toISODate(d) === toISODate(selectedDate))
    );

    const sidebarWeeks = getWeeksOfMonth(sidebarYear, sidebarMonth);
    const sunday = getSunday(selectedDate);
    const weekDates = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + i);
        return date;
    });
    const todayIdx = weekDates.findIndex(
        (d) => d.toDateString() === today.toDateString()
    );
    const isTodayInWeek = todayIdx !== -1;

    const dayStartHour = 0;
    const dayEndHour = 24;
    const totalMinutes = (dayEndHour - dayStartHour) * 60;
    const numRows = (dayEndHour - dayStartHour);
    const rowHeight = 60;
    const numDays = 7;

    useEffect(() => {
        setTimeout(() => {
            const scrollContainer = document.querySelector('.overflow-y-auto');
            if (scrollContainer) {
                const scrollToY = 8 * rowHeight;
                scrollContainer.scrollTop = scrollToY;
            }
        }, 100);
    }, []);

    const weekISO = weekDates.map(toISODate);
    const weekNumber = Math.ceil((new Date(weekDates[0]).getDate() - 1 + new Date(weekDates[0]).getDay()) / 7);
    const isOddWeek = weekNumber % 2 === 1;
    const isEvenWeek = weekNumber % 2 === 0;
    const unavailEvents = unavailability.filter((u) => {
        if (u.repeat === "none") return weekISO.includes(u.date);
        if (u.repeat === "weekly") return weekDates.some((d) => toISODate(d) >= u.date);
        if (u.repeat === "odd") return isOddWeek && weekDates.some((d) => toISODate(d) >= u.date);
        if (u.repeat === "even") return isEvenWeek && weekDates.some((d) => toISODate(d) >= u.date);
        return false;
    });

    const availEvents = availability.flatMap((a) => {
        let days = [];
        const orig = new Date(a.date);
        weekDates.forEach((d, idx) => {
            if (
                (a.repeat === "none" && localDateString(d) === a.date) ||
                (a.repeat === "weekly" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig)) ||
                (a.repeat === "odd" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig) && isOddWeek) ||
                (a.repeat === "even" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig) && isEvenWeek)
            ) {
                days.push({
                    date: localDateString(d),
                    start: a.allDay ? 0 : timeToDecimal(a.start),
                    end: a.allDay ? 24 : timeToDecimal(a.end),
                    title: "Available",
                    color: "border-green-400 bg-green-100",
                    text: "text-green-700",
                    isAvailable: true,
                    originalEvent: a,
                    startRaw: a.start,
                    endRaw: a.end,
                    repeat: a.repeat,
                    allDay: a.allDay,
                });
            }
        });
        return days;
    });
    const assignmentEvents = [];

    assignments.forEach(appt => {
        let start = 9, end = 10;
        if (appt.start_time && appt.end_time) {
            const [sh, sm] = appt.start_time.split(":").map(Number);
            const [eh, em] = appt.end_time.split(":").map(Number);
            start = sh + (sm || 0) / 60;
            end = eh + (em || 0) / 60;
        } else if (appt.timestamp) {
            const dt = new Date(appt.timestamp);
            start = dt.getHours() + dt.getMinutes() / 60;
            end = start + 1;
        }

        const centerName = centerMap[appt.center_id] || appt.center_id;
        const isOvernight = end < start;

        if (isOvernight) {
            assignmentEvents.push({
                date: appt.date,
                start,
                end: 24,
                centerName,
                room_name: appt.room_name,
                details: appt.details,
                color: "border-blue-400",
                text: "text-blue-700",
                startRaw: appt.start_time,
                endRaw: appt.end_time,
                assignment_type: appt.assignment_type,
                isOvernight: true,
                isOvernightPart: 'first'
            });

            const nextDate = new Date(appt.date);
            nextDate.setDate(nextDate.getDate() + 1);
            const nextDateStr = toISODate(nextDate);

            assignmentEvents.push({
                date: nextDateStr,
                start: 0,
                end,
                centerName,
                room_name: appt.room_name,
                details: appt.details,
                color: "border-blue-400",
                text: "text-blue-700",
                startRaw: appt.start_time,
                endRaw: appt.end_time,
                assignment_type: appt.assignment_type,
                isOvernight: true,
                isOvernightPart: 'second'
            });
        } else {
            assignmentEvents.push({
                date: appt.date,
                start,
                end,
                centerName,
                room_name: appt.room_name,
                details: appt.details,
                color: "border-blue-400",
                text: "text-blue-700",
                startRaw: appt.start_time,
                endRaw: appt.end_time,
                assignment_type: appt.assignment_type,
            });
        }
    });
    const allEvents = [
        ...assignmentEvents,
        ...availEvents,
        ...unavailEvents.flatMap((u) => {
            let days = [];
            const orig = new Date(u.date);
            weekDates.forEach((d, idx) => {
                if (
                    (u.repeat === "none" && localDateString(d) === u.date) ||
                    (u.repeat === "weekly" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig)) ||
                    (u.repeat === "odd" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig) && isOddWeek) ||
                    (u.repeat === "even" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig) && isEvenWeek)
                ) {
                    days.push({
                        date: localDateString(d),
                        start: u.allDay ? 0 : timeToDecimal(u.start),
                        end: u.allDay ? 24 : timeToDecimal(u.end),
                        title: "Not available",
                        color: "border-red-300 bg-red-100",
                        text: "text-red-700",
                        isUnavailable: true,
                        originalEvent: u,
                        startRaw: u.start,
                        endRaw: u.end,
                        repeat: u.repeat,
                        allDay: u.allDay,
                    });
                }
            });
            return days;
        }),
    ];







    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setEventDetailsModal(true);
    };

    function isCurrentWeek() {
        const todaySunday = getSunday(today);
        return toISODate(getSunday(selectedDate)) === toISODate(todaySunday);
    }

    const weekRange = `${formatDateFull(weekDates[0])} - ${formatDateFull(weekDates[6])}`;

    return (
        <div className="flex h-screen w-full bg-transparent overflow-hidden">
            <div className="w-64 bg-white shadow-lg h-full flex flex-col min-h-0 justify-between overflow-hidden">
                <div>
                    <div className="flex items-center justify-center px-4 py-4">
                        <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Weekly Schedule</span>
                    </div>
                    <div className="p-4 min-h-0 overflow-hidden">
                        <div className="flex items-center justify-center mb-2">
                            <button
                                className="p-1 rounded hover:bg-gray-200"
                                aria-label="Previous month"
                                onClick={e => {
                                    e.stopPropagation();
                                    if (sidebarMonth === 0) {
                                        setSidebarMonth(11);
                                        setSidebarYear(sidebarYear - 1);
                                    } else {
                                        setSidebarMonth(sidebarMonth - 1);
                                    }
                                }}
                            >
                                <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
                            </button>
                            <div className="text-lg font-bold mx-2 text-center">
                                {new Date(sidebarYear, sidebarMonth).toLocaleString("default", { month: "long" })} {sidebarYear}
                            </div>
                            <button
                                className="p-1 rounded hover:bg-gray-200"
                                aria-label="Next month"
                                onClick={e => {
                                    e.stopPropagation();
                                    if (sidebarMonth === 11) {
                                        setSidebarMonth(0);
                                        setSidebarYear(sidebarYear + 1);
                                    } else {
                                        setSidebarMonth(sidebarMonth + 1);
                                    }
                                }}
                            >
                                <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-xs text-center mb-1">
                            {days.map((d) => (
                                <div key={d} className="font-semibold text-gray-500">{d}</div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-1">
                            {sidebarWeeks.map((week, wIdx) => (
                                <div
                                    key={wIdx}
                                    className={`grid grid-cols-7 gap-1 rounded cursor-pointer transition-colors
                    ${wIdx === selectedWeekIdx && sidebarMonth === month && sidebarYear === year ? "bg-blue-100" : ""}
                    ${hoveredWeekIdx === wIdx ? "bg-blue-50" : ""}`}
                                    onMouseEnter={() => setHoveredWeekIdx(wIdx)}
                                    onMouseLeave={() => setHoveredWeekIdx(null)}
                                    onClick={() => {
                                        const sunday = week.find((d, i) => i === 0 && d) || week.find((d) => d);
                                        if (sunday) {
                                            setSelectedDate(sunday);
                                            setSidebarMonth(sunday.getMonth());
                                            setSidebarYear(sunday.getFullYear());
                                        }
                                    }}
                                >
                                    {week.map((date, i) => {
                                        const isToday = date && toISODate(date) === toISODate(today);
                                        return (
                                            <div
                                                key={i}
                                                className={`rounded-full h-7 w-7 flex items-center justify-center mx-auto
                          ${isToday ? "bg-blue-500 text-white font-bold" : "text-gray-700"}
                          ${date ? "" : ""}`}
                                            >
                                                {date ? date.getDate() : ""}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-4 flex flex-col gap-2 bg-white z-10 border-t mb-12">
                    {!isCurrentWeek() && (
                        <button
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 font-semibold transition text-white rounded"
                            style={{ height: 'auto', minHeight: '48px' }}
                            onClick={() => setSelectedDate(getSunday(today))}
                        >
                            Go back to this week
                        </button>
                    )}
                    <button
                        className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 font-semibold transition text-white rounded"
                        style={{ height: 'auto', minHeight: '48px' }}
                        onClick={() => setIsMonthlyView(!isMonthlyView)}
                    >
                        {isMonthlyView ? 'Switch to Weekly View' : 'Switch to Monthly View'}
                    </button>
                                         <button
                         className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 font-normal font-semibold transition text-white rounded"
                         style={{ height: 'auto', minHeight: '48px' }}
                         onClick={() => setContactModalOpen(true)}
                     >
                         Add Availability/Unavailability
                     </button>
                </div>
            </div>
            {contactModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-md p-6 rounded shadow relative">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => setContactModalOpen(false)}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>
                        <h2 className="text-xl font-bold mb-4">Add Availability/Unavailability</h2>
                        <div className="space-y-4">
                            <p className="text-gray-700">
                                To add your availability/unavailability info, please contact your manager at{' '}
                                <a 
                                    href="mailto:brianha@mac.com" 
                                    className="text-blue-600 hover:text-blue-800 underline"
                                >
                                    brianha@mac.com
                                </a>
                            </p>
                            <div className="flex justify-end mt-4">
                                <button
                                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded border-none"
                                    style={{ minWidth: '80px', height: 'auto' }}
                                    onClick={() => setContactModalOpen(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}



            {eventDetailsModal && selectedEvent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-md p-6 rounded shadow relative">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => {
                                setEventDetailsModal(false);
                                setSelectedEvent(null);
                            }}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>
                        <h2 className="text-xl font-bold mb-4">Assignment Details</h2>
                        <div className="space-y-3">
                            <div>
                                <span className="font-medium text-gray-700">Center:</span>
                                <span className="ml-2 text-gray-900">{selectedEvent.centerName}</span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-700">Date:</span>
                                <span className="ml-2 text-gray-900">{selectedEvent.date}</span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-700">Time:</span>
                                <span className="ml-2 text-gray-900">
                                    {formatTimeStr(selectedEvent.startRaw)} - {formatTimeStr(selectedEvent.endRaw)}
                                </span>
                            </div>
                            {selectedEvent.room_name && (
                                <div>
                                    <span className="font-medium text-gray-700">Room:</span>
                                    <span className="ml-2 text-gray-900">{selectedEvent.room_name}</span>
                                </div>
                            )}
                            {selectedEvent.assignment_type && (
                                <div>
                                    <span className="font-medium text-gray-700">Type:</span>
                                    <span className="ml-2 text-gray-900">
                                        {selectedEvent.assignment_type === 'shift' ? (
                                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-sm rounded font-medium">SHIFT</span>
                                        ) : (
                                            selectedEvent.assignment_type
                                        )}
                                    </span>
                                </div>
                            )}
                            {selectedEvent.details && (
                                <div>
                                    <span className="font-medium text-gray-700">Details:</span>
                                    <span className="ml-2 text-gray-900">{selectedEvent.details}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}


            <div className="flex-1 flex flex-col h-full min-h-0 w-full overflow-hidden">
                <div className="flex items-center justify-between h-14 px-4 bg-white border-b shadow-sm sticky top-0 z-20">
                    {isMonthlyView ? (
                        <>
                            <button
                                className="p-2 rounded hover:bg-gray-100"
                                onClick={() => setMonthlyViewDate(addDays(monthlyViewDate, -30))}
                                aria-label="Previous month"
                            >
                                <ChevronLeftIcon className="h-6 w-6 text-gray-500" />
                            </button>
                            <span className="font-semibold text-lg">
                                {monthlyViewDate.toLocaleDateString('en-US', {
                                    month: 'long',
                                    year: 'numeric'
                                })}
                            </span>
                            <button
                                className="p-2 rounded hover:bg-gray-100"
                                onClick={() => setMonthlyViewDate(addDays(monthlyViewDate, 30))}
                                aria-label="Next month"
                            >
                                <ChevronRightIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className="p-2 rounded hover:bg-gray-100"
                                onClick={() => setSelectedDate(addDays(selectedDate, -7))}
                                aria-label="Previous week"
                            >
                                <ChevronLeftIcon className="h-6 w-6 text-gray-500" />
                            </button>
                            <span className="font-semibold text-lg">{weekRange}</span>
                            <button
                                className="p-2 rounded hover:bg-gray-100"
                                onClick={() => setSelectedDate(addDays(selectedDate, 7))}
                                aria-label="Next week"
                            >
                                <ChevronRightIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </>
                    )}
                </div>
                {isMonthlyView ? (
                    <div className="flex w-full bg-white border-b" style={{ height: 48 }}>
                        {days.map((day, idx) => (
                            <div
                                key={idx}
                                className="flex-1 flex flex-col items-center justify-center border-r last:border-r-0"
                                style={{ minWidth: 0 }}
                            >
                                <span className="font-semibold text-base text-gray-700">{day}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex w-full bg-white border-b" style={{ height: 48 }}>
                        <div className="w-16" />
                        {weekDates.map((date, idx) => (
                            <div
                                key={idx}
                                className="flex-1 flex flex-col items-center justify-center border-r last:border-r-0"
                                style={{ minWidth: 0 }}
                            >
                                <span className="font-semibold text-base text-gray-700">{days[idx]}</span>
                                <span className="text-sm text-gray-500">{date.getDate()}</span>
                            </div>
                        ))}
                    </div>
                )}
                {isMonthlyView ? (
                    <div className="relative flex-1 w-full bg-white overflow-y-auto" style={{ minHeight: 0 }}>
                        <div className="p-4">
                            <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-lg">
                                {(() => {
                                    const monthlyWeeks = getWeeksOfMonth(monthlyViewDate.getFullYear(), monthlyViewDate.getMonth());
                                    return monthlyWeeks.map((week, weekIdx) => 
                                        week.map((date, dayIdx) => {
                                            if (!date) {
                                                return (
                                                    <div key={`${weekIdx}-${dayIdx}`} className="bg-white p-2 min-h-[120px]">
                                                    </div>
                                                );
                                            }
                                            
                                            const dateStr = toISODate(date);
                                            const dayAssignments = monthlyAssignments.filter(appt => appt.date === dateStr);
                                            const isToday = date.toDateString() === today.toDateString();
                                            
                                            return (
                                                <div 
                                                    key={`${weekIdx}-${dayIdx}`} 
                                                    className={`bg-white p-2 min-h-[120px] ${isToday ? 'bg-blue-50' : ''}`}
                                                >
                                                    <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                                                        {date.getDate()}
                                                    </div>
                                                    <div className="space-y-1">
                                                        {dayAssignments.slice(0, 3).map((appt, idx) => {
                                                            const centerName = centerMap[appt.center_id] || appt.center_id;
                                                            const startTime = appt.start_time || '';
                                                            const endTime = appt.end_time || '';
                                                            
                                                            const event = {
                                                                centerName,
                                                                date: appt.date,
                                                                startRaw: startTime,
                                                                endRaw: endTime,
                                                                room_name: appt.room_name,
                                                                assignment_type: appt.assignment_type,
                                                                details: appt.details
                                                            };
                                                            
                                                            const eventColor = getEventColor(event);
                                                            const colorMap = COLOR_CLASSES;
                                                            const palette = colorMap[eventColor] || colorMap.gray;
                                                            
                                                            return (
                                                                <div 
                                                                    key={idx}
                                                                    className={`text-xs px-2 py-1 rounded truncate cursor-pointer border-l-4 ${palette.border} ${palette.bg} ${palette.text} shadow-sm hover:shadow-md transition-all`}
                                                                    onClick={() => {
                                                                        setSelectedEvent(event);
                                                                        setEventDetailsModal(true);
                                                                    }}
                                                                >
                                                                    <div className="font-medium truncate">{centerName}</div>
                                                                    <div className="text-xs">
                                                                        {startTime && endTime ? `${formatTimeStr(startTime)} - ${formatTimeStr(endTime)}` : 'All day'}
                                                                    </div>
                                                                    {appt.assignment_type === 'shift' && (
                                                                        <div className="text-xs font-medium">SHIFT</div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                        {dayAssignments.length > 3 && (
                                                            <div className="text-xs text-gray-500">
                                                                +{dayAssignments.length - 3} more
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ).flat();
                                })()}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="relative flex-1 w-full bg-white overflow-y-auto" style={{ minHeight: 0 }}>
                        <div className="absolute left-0 top-0 w-16" style={{ height: numRows * rowHeight }}>
                            {Array.from({ length: dayEndHour - dayStartHour }, (_, i) => {
                                const hour = dayStartHour + i;
                                const top = i * rowHeight;
                                return (
                                    <div
                                        key={hour}
                                        className="absolute text-xs text-gray-500"
                                        style={{ top, height: rowHeight, lineHeight: `${rowHeight}px` }}
                                    >
                                        {hour % 12 === 0 ? 12 : hour % 12}:00 {hour < 12 ? 'AM' : 'PM'}
                                    </div>
                                );
                            })}
                        </div>
                    <div className="absolute left-16 top-0 right-0" style={{ height: numRows * rowHeight }}>
                        {weekDates.map((_, idx) => (
                            <div
                                key={idx}
                                className="absolute top-0 bottom-0 border-r border-gray-200"
                                style={{ left: `calc(${idx} / ${numDays} * 100%)`, width: 1, zIndex: 1 }}
                            />
                        ))}
                        {Array.from({ length: numRows + 1 }, (_, i) => {
                            const top = i * rowHeight;
                            return (
                                <div
                                    key={i}
                                    className="absolute left-0 right-0 border-b border-gray-200"
                                    style={{ top, height: 0, zIndex: 1 }}
                                />
                            );
                        })}
                        {weekDates.map((date, dayIdx) => {
                            const dayEvents = allEvents.filter((e) => e.date === localDateString(date));
                            const availUnavailEvents = dayEvents.filter(e => e.isAvailable || e.isUnavailable);
                            const otherEvents = dayEvents.filter(e => !e.isAvailable && !e.isUnavailable);
                            return (
                                <div
                                    key={dayIdx}
                                    className="absolute top-0 h-full"
                                    style={{
                                        left: `calc(${dayIdx} / ${numDays} * 100%)`,
                                        width: `calc(100% / ${numDays})`,
                                        zIndex: 2,
                                    }}
                                >
                                    <div className="relative w-full h-full">
                                        {availUnavailEvents.map((event, i) => {
                                            const eventStartHours = event.start - dayStartHour;
                                            const eventEndHours = event.end - dayStartHour;
                                            const top = eventStartHours * rowHeight;
                                            const height = (eventEndHours - eventStartHours) * rowHeight;
                                            const colorMap = COLOR_CLASSES;
                                            let colorKey = 'gray';
                                            if (event.color && event.color.includes('blue')) colorKey = 'blue';
                                            else if (event.color && event.color.includes('green')) colorKey = 'green';
                                            else if (event.color && event.color.includes('pink')) colorKey = 'pink';
                                            else if (event.color && event.color.includes('yellow')) colorKey = 'yellow';
                                            else if (event.color && event.color.includes('red')) colorKey = 'red';
                                            const palette = colorMap[colorKey];
                                            return (
                                                <div
                                                    key={i}
                                                    className={`absolute px-3 py-2 flex flex-col items-start font-semibold text-sm border-l-4 ${palette.border} ${palette.bg} ${palette.text} overflow-hidden whitespace-pre-line rounded shadow-sm backdrop-blur-sm`}
                                                    style={{
                                                        left: 0,
                                                        right: 0,
                                                        top,
                                                        height,
                                                        maxWidth: '100%',
                                                        zIndex: 5,
                                                        color: event.isUnavailable ? '#b91c1c' : undefined,
                                                        margin: '0px',
                                                        backgroundClip: 'padding-box',
                                                        transform: hoveredAvailUnavail === event ? 'scale(1.05)' : 'scale(1)',
                                                    }}
                                                    onMouseEnter={() => setHoveredAvailUnavail(event)}
                                                    onMouseLeave={() => setHoveredAvailUnavail(null)}

                                                >
                                                    {event.title}
                                                    <span
                                                        className={`text-xs font-normal mt-1 ${event.isUnavailable ? 'text-red-300' : event.isAvailable ? 'text-green-400' : 'text-gray-400'}`}
                                                        style={{ fontWeight: 400 }}
                                                    >
                                                        {formatTimeStr(event.startRaw || event.startStr || event.startStrRaw || event.startStrValue || event.startStrTime || event.startStr || (event.start < 10 ? `0${event.start}:00` : `${Math.floor(event.start)}:${(event.start % 1 === 0.5 ? '30' : '00')}`))}
                                                        {' - '}
                                                        {formatTimeStr(event.endRaw || event.endStr || event.endStrRaw || event.endStrValue || event.endStrTime || event.endStr || (event.end < 10 ? `0${event.end}:00` : `${Math.floor(event.end)}:${(event.end % 1 === 0.5 ? '30' : '00')}`))}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        {otherEvents.map((event, i) => {
                                            const eventStartHours = event.start - dayStartHour;
                                            const eventEndHours = event.end - dayStartHour;
                                            const top = eventStartHours * rowHeight;
                                            const height = (eventEndHours - eventStartHours) * rowHeight;

                                            const eventColor = getEventColor(event);

                                            const colorMap = COLOR_CLASSES;
                                            const palette = colorMap[eventColor] || colorMap.gray;

                                            const position = calculateEventPosition(event, i, otherEvents);
                                            const overlapsWithAvail = availUnavailEvents.some(av =>
                                                event.start < av.end && event.end > av.start
                                            );
                                            const isHovered = hoveredEvent === `${dayIdx}-${i}`;

                                            return (
                                                <div
                                                    key={i}
                                                    className={`absolute px-3 py-2 flex flex-col items-start font-semibold text-sm border-l-4 ${palette.border} ${palette.bg} ${palette.text} overflow-hidden whitespace-pre-line rounded shadow-sm backdrop-blur-sm cursor-pointer hover:shadow-lg`}
                                                    style={{
                                                        left: overlapsWithAvail ? 8 : (position.isConflicting ? position.left : 0),
                                                        right: overlapsWithAvail ? 8 : (position.isConflicting ? 'auto' : 0),
                                                        width: overlapsWithAvail ? 'calc(100% - 16px)' : (position.isConflicting ? position.width : '100%'),
                                                        top,
                                                        height,
                                                        zIndex: isHovered ? 100 : position.zIndex,
                                                        margin: position.isConflicting ? '1px' : '3px',
                                                        backgroundClip: 'padding-box',
                                                        justifyContent: position.textAlign === 'bottom' ? 'flex-end' : 'flex-start',
                                                    }}
                                                    onMouseEnter={() => setHoveredEvent(`${dayIdx}-${i}`)}
                                                    onMouseLeave={() => setHoveredEvent(null)}
                                                    onClick={() => handleEventClick(event)}
                                                >
                                                    {event.centerName ? (
                                                        <>
                                                            <span className="text-xs font-semibold">
                                                                {event.centerName}
                                                                {event.assignment_type === 'shift' && (
                                                                    <span className="ml-1 px-1 py-0.5 bg-purple-100 text-purple-800 text-xs rounded font-medium">
                                                                        {getShiftType(event.startRaw, event.endRaw)}
                                                                    </span>
                                                                )}
                                                                {event.isOvernight && (
                                                                    <span className="ml-1 px-1 py-0.5 bg-orange-100 text-orange-800 text-xs rounded font-medium">
                                                                        OVERNIGHT
                                                                    </span>
                                                                )}
                                                                {event.room_name ? `\nRoom: ${event.room_name}` : ''}
                                                            </span>
                                                            {event.details && (
                                                                <span className="text-xs font-normal text-gray-500 mt-1" style={{ fontWeight: 400 }}><b>Details:</b> {event.details}</span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        event.title
                                                    )}
                                                    <span className="text-xs font-normal mt-1 text-gray-400" style={{ fontWeight: 400 }}>
                                                        {formatTimeStr(event.startRaw || event.startStr || event.startStrRaw || event.startStrValue || event.startStrTime || event.startStr || (event.start < 10 ? `0${event.start}:00` : `${Math.floor(event.start)}:${(event.start % 1 === 0.5 ? '30' : '00')}`))}
                                                        {' - '}
                                                        {formatTimeStr(event.endRaw || event.endStr || event.endStrRaw || event.endStrValue || event.endStrTime || event.endStr || (event.end < 10 ? `0${event.end}:00` : `${Math.floor(event.end)}:${(event.end % 1 === 0.5 ? '30' : '00')}`))}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                )}
            </div>
        </div>
    );
};

export default Schedule; 